import React, { useContext, useState, useEffect } from "react";
import {
  Grid2 as Grid,
  Stack,
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import LinearProgress from "@mui/material/LinearProgress";
import { useMutation } from "@apollo/client";

//Icons
import { BiMinusCircle } from "react-icons/bi";


//Srcs
import "../../Style/dialogStyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { translateLauguage } from "../../Function/Translate";
import { UPDATE_USER } from "../../Schema/Users";

function LinearProgressWithLabel(props) {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box sx={{ width: "100%", mr: 1 }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" color="text.secondary">{`${Math.round(
          props.value
        )}%`}</Typography>
      </Box>
    </Box>
  );
}
LinearProgressWithLabel.propTypes = {
  value: PropTypes.number.isRequired,
};

export const UpdateUser = ({ open, handleClose, editData, setRefetch }) => {
  // console.log("editData==>", editData);

  // ===================== Change Language ==========================
  const { language, setAlert } = useContext(AuthContext);
  const { t } = translateLauguage(language);

  const [loading, setLoading] = useState(false);

  //======================== update User =========================
  const [updateUser] = useMutation(UPDATE_USER, {
    onCompleted: ({ updateUser }) => {
      // console.log("updateUser::", updateUser);
      setLoading(false);
      if (updateUser?.is_success === true) {
        setAlert(true, "success", updateUser?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateUser?.message);
      }
    },
    onError: (error) => {
      setLoading(false);
      setAlert(true, "error", error?.message);
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    first_name: Yup.string().required(t("require")),
    last_name: Yup.string().required(t("require")),
    gender: Yup.string().required(t("require")),
    phone_number: Yup.string(),
    role: Yup.string().required(t("require")),
    email: Yup.string().email().required(t("require")),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      first_name: "",
      last_name: "",
      latin_name: "",
      gender: "Male",
      phone_number: "",
      role: "Admin",
    },

    validationSchema: CheckValidation,
    onSubmit: async (submitValues) => {
      // console.log("submitValues:::", submitValues);
      setLoading(true);
      updateUser({
        variables: {
          id: editData?._id,
          input: {
            ...submitValues,
          },
        },
      });
    },
  });

  useEffect(() => {
    if (editData) {
      setFieldValue("first_name", editData?.first_name);
      setFieldValue("last_name", editData?.last_name);
      setFieldValue("latin_name", editData?.latin_name);
      setFieldValue("gender", editData?.gender);
      setFieldValue("phone_number", editData?.phone_number);
      setFieldValue("email", editData?.email);
      setFieldValue("role", editData?.role);
    }
  }, [open, editData]);

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    resetForm,
    setFieldValue,
  } = formik;

  return (
    <Dialog open={open} className="dialog-container" fullWidth maxWidth="sm">
      <DialogTitle sx={{ padding: "10px 15px 10px 20px " }}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column" justifyContent="center">
            <Typography
              className={language === "en" ? "dialog-title" : "dialog-title-kh"}
            >
              {t("dialog_title_update_user")}
            </Typography>
          </Stack>
          <Stack direction="column" justifyContent="center">
            <IconButton onClick={handleClose}>
              <BiMinusCircle className="close-icon" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("label_last_name")}(KH)
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("label_last_name")}
                  {...getFieldProps("last_name")}
                  error={Boolean(touched.last_name && errors.last_name)}
                  helperText={touched.last_name && errors.last_name}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("label_first_name")}(KH)
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("label_first_name")}
                  {...getFieldProps("first_name")}
                  error={Boolean(touched.first_name && errors.first_name)}
                  helperText={touched.first_name && errors.first_name}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("label_latin_name")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("label_latin_name")}
                  {...getFieldProps("latin_name")}
                  error={Boolean(touched.latin_name && errors.latin_name)}
                  helperText={touched.latin_name && errors.latin_name}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("label_gender")}
                </Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={values?.gender}
                    onChange={(e) => setFieldValue("gender", e.target.value)}
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("label_tel")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("label_tel")}
                  {...getFieldProps("phone_number")}
                  error={Boolean(touched.phone_number && errors.phone_number)}
                  helperText={touched.phone_number && errors.phone_number}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("label_role")}
                </Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={values?.role}
                    onChange={(e) => setFieldValue("role", e.target.value)}
                  >
                    <MenuItem value="Admin">Admin</MenuItem>
                    <MenuItem value="Teacher">Teacher</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("label_email")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("label_email")}
                  {...getFieldProps("email")}
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                />
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
      <DialogActions>
        <Grid size={{ xs: 12 }} className="create-container">
          {loading ? (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
            >
              {t("loading")}
            </Button>
          ) : (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
              onClick={handleSubmit}
            >
              {t("btn-update")}
            </Button>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
};
